import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { EzLogo, NavMobileToggle, NavDesktopLinks } from "@/pages/LandingPage";
import { CheckCircle2, ArrowLeft, Mail, Quote, Star } from "lucide-react";
import mixpanel from "@/mixpanel";

export default function ThankYou() {
  const navigate = useNavigate();

  useEffect(() => { mixpanel.track("thankYouPageViewed"); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="thank-you-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <EzLogo size="h-7" />
            <NavDesktopLinks />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
            className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </motion.div>

          <h1 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-4" data-testid="thank-you-title">
            Application Submitted!
          </h1>
          <p className="text-base md:text-lg text-slate-600 font-body leading-relaxed mb-8">
            Thank you for applying to become a Health Guide at Ez Health.
            We're thrilled by your interest — our team will review your application and reach out soon.
          </p>

          <div className="bg-ez-surface rounded-2xl border border-slate-100 p-6 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-ez-blue flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-ez-teal text-sm font-heading mb-1">What happens next?</p>
                <p className="text-sm text-slate-600 font-body leading-relaxed">
                  Our team will review your answers &amp; resume. If your background is a match,
                  we will reach out with next steps within 2 business days.
                </p>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="bg-white rounded-2xl border border-slate-100 p-6 mb-10 text-left shadow-sm"
            data-testid="thank-you-testimonial"
          >
            <Quote className="w-6 h-6 text-ez-peach/40 mb-2" />
            <p className="text-slate-700 font-body italic text-sm leading-relaxed mb-3">
              "I applied on a whim after 10 years as a health coach. Two weeks later I was in training,
              and within a month I was helping real people solve real problems. The onboarding is incredible
              and the team genuinely wants you to succeed."
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-ez-blue/10 flex items-center justify-center">
                  <span className="text-ez-blue font-bold text-xs font-heading">M</span>
                </div>
                <div>
                  <p className="font-semibold text-ez-teal text-xs font-heading">Maria R.</p>
                  <p className="text-[11px] text-slate-500 font-body">Health Guide · Former Health Coach</p>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-ez-peach text-ez-peach" />
                ))}
              </div>
            </div>
          </motion.div>

          <Button
            data-testid="back-to-home-btn"
            variant="outline"
            onClick={() => navigate("/")}
            className="rounded-full border-2 border-ez-blue text-ez-blue hover:bg-blue-50 px-8 py-3 font-semibold"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Homepage
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
